import { TUNING, type GameState, type LineClearEvent } from '../core/game.ts';
import type { Cell } from '../core/board.ts';
import { PIECE_COLORS } from '../core/piece.ts';
import type { Painter } from './painter.ts';

interface Debris {
  x: number;
  y: number;
  vx: number;
  vy: number;
  rotation: number;
  spin: number;
  color: string;
  scale: number;
}

const GRAVITY = 24;

/** Renderer-only impact for clearing two or more rows at once. */
export class ClearImpact {
  private debris: Debris[] = [];
  private elapsedMs = 0;
  private lifeMs = 0;
  private lines = 0;

  trigger(state: GameState, event: LineClearEvent): void {
    if (event.lines < 2) return;

    const cells = state.board.snapshot();
    const width = state.board.width;
    const centerX = width / 2;
    const impact = event.lines - 1;
    const seed = event.lines * 37 + event.rows.reduce((sum, row) => sum + row, 0);
    const next = mulberry32(seed);
    const particles: Debris[] = [];

    for (const row of event.rows) {
      for (let x = 0; x < width; x++) {
        const cell: Cell = cells[row * width + x];
        if (!cell) continue;

        const away = (x + 0.5 - centerX) / centerX;
        particles.push({
          x: x + 0.5,
          y: row - TUNING.board.bufferHeight + 0.5,
          vx: away * (3.5 + impact * 2.2 + next() * (2 + impact)) +
            (next() - 0.5) * (1.5 + impact),
          vy: -(3.5 + impact * 2.1 + next() * (4 + impact * 1.7)),
          rotation: (next() - 0.5) * 0.7,
          spin: (next() - 0.5) * (8 + impact * 3),
          color: PIECE_COLORS[cell],
          scale: 0.48 + impact * 0.05 + next() * 0.3,
        });
      }
    }

    this.debris = particles;
    this.elapsedMs = 0;
    this.lifeMs = 540 + impact * 150;
    this.lines = event.lines;
  }

  update(dtMs: number): void {
    if (this.debris.length === 0) return;
    this.elapsedMs += Math.max(0, dtMs);
    if (this.elapsedMs >= this.lifeMs) this.debris = [];
  }

  /** Offset in logical canvas pixels; apply it to the field or board body. */
  shakeOffset(): { x: number; y: number } {
    const impact = Math.max(0, this.lines - 1);
    const duration = 170 + impact * 85;
    if (this.debris.length === 0 || this.elapsedMs >= duration) return { x: 0, y: 0 };

    const progress = this.elapsedMs / duration;
    const strength = (1.5 + impact ** 2 * 1.25) * (1 - progress) ** 2;
    return {
      x: (Math.sin(this.elapsedMs * 0.19) + Math.sin(this.elapsedMs * 0.071) * 0.45) * strength,
      y: (Math.cos(this.elapsedMs * 0.23) + Math.sin(this.elapsedMs * 0.097) * 0.35) * strength * 0.65,
    };
  }

  /** Draw in board space after the field clip has been released. */
  draw(painter: Painter, fieldX: number, fieldY: number, cellSize: number): void {
    if (this.debris.length === 0) return;

    const ctx = painter.ctx;
    const seconds = this.elapsedMs / 1000;
    const progress = Math.min(1, this.elapsedMs / this.lifeMs);
    const alpha = progress < 0.58 ? 1 : (1 - progress) / 0.42;

    ctx.save();
    ctx.globalAlpha = Math.max(0, alpha);
    for (const bit of this.debris) {
      const x = fieldX + (bit.x + bit.vx * seconds) * cellSize;
      const y = fieldY + (bit.y + bit.vy * seconds + 0.5 * GRAVITY * seconds ** 2) * cellSize;
      const size = cellSize * bit.scale * (1 - progress * 0.42);

      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(bit.rotation + bit.spin * seconds);
      painter.blockCell(-size / 2, -size / 2, size, bit.color);
      ctx.restore();
    }
    ctx.restore();
  }
}

/** Small deterministic PRNG so identical clears produce stable motion. */
function mulberry32(seed: number): () => number {
  let value = seed >>> 0;
  return () => {
    value += 0x6d2b79f5;
    let n = value;
    n = Math.imul(n ^ (n >>> 15), n | 1);
    n ^= n + Math.imul(n ^ (n >>> 7), n | 61);
    return ((n ^ (n >>> 14)) >>> 0) / 0x1_0000_0000;
  };
}
